import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import useAuth from './useAuth';
import { QUERY_KEYS } from 'src/constants/queries';
import { AI_CHAT_SCOPES, hasScope } from 'src/constants/scopes';
import useApi from './useApi';
import { Auth } from 'src/api/Auth';
import useTeams from './useTeams';
import { extractDataFromToken } from 'src/utils/tokens';
import useFeatures from './useFeatures';
import { KNOWN_FEATURES } from 'src/constants/features';

/**
 * Exchanges the platform session for a token the chat backend will accept, and
 * reports what that token is allowed to do.
 *
 * The chat backend verifies `aud` against `com.effektify.ai-chat`, while
 * one-app's own `access_token` sits on the `com.effektify.platform` axis — so
 * the session token is rejected outright and this exchange is the only way in.
 *
 * Shaped after `useAdStudioAuth`, which does the same thing for `ad-studio`.
 */
export const useAiChatAuth = () => {
  const {
    isAuthenticated
  } = useAuth();
  const authApi = useApi(Auth);
  const {
    currentTeam
  } = useTeams();
  const {
    isEnabled
  } = useFeatures();
  const chatEnabled = isEnabled(KNOWN_FEATURES.ASK_ME_ANYTHING, false);
  const {
    data: aiChatToken,
    isLoading: loadingAiChatAuth,
    isStale,
    error,
    refetch
  } = useQuery({
    // The team id belongs in the key: the token is minted for one team, and
    // switching teams must mint a new one rather than reuse the last team's.
    queryKey: [QUERY_KEYS.AUTH.AI_CHAT, currentTeam?.id],
    queryFn: async () => {
      const response = await authApi.authService(currentTeam!.id, 'ai-chat');
      const {
        data
      } = response;
      if (!data || !data.success) {
        throw new Error('Invalid token');
      }
      return data.jwt;
    },
    enabled: !!isAuthenticated && !!currentTeam && chatEnabled,
    staleTime: 1000 * 60 * 15
  });

  /**
   * What the token says this user may do in this team.
   *
   * The grant lives on the token rather than in a separate call: Core puts it in
   * `teams['team:<ULID>'].privileges`, keyed by the tagged team reference even
   * though `currentTeam.id` is bare.
   *
   * `extractDataFromToken` throws on a payload it cannot parse, and this runs
   * during render — so a malformed token would take the page down rather than
   * just hiding the chat. Not a real risk for a token we just minted, but the
   * cost of being wrong is out of proportion to the cost of the guard.
   */
  const availableScopes = useMemo(() => {
    if (!aiChatToken?.length) return [];
    try {
      const privileges = extractDataFromToken(aiChatToken)?.teams?.[`team:${currentTeam!.id}`]?.privileges;
      if (!privileges) return [];
      return Object.keys(privileges).filter(key => privileges[key]?.allowed);
    } catch {
      return [];
    }
  }, [aiChatToken, currentTeam]);
  return {
    aiChatToken,
    aiChatTokenStale: isStale,
    refetchAiChatToken: refetch,
    loadingAiChatAuth,
    aiChatAuthError: error,
    availableScopes,
    canPost: hasScope(availableScopes, AI_CHAT_SCOPES.POST)
  };
};